import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, SwitcherButtonProps } from "components/buttons/Button";
import SwitcherLogo from "assets/SwitcherPlayerIconGradient.svg?react";
import styles from "./index.module.scss";
import { useNavigate } from "react-router-dom";

export type ErrorPageVariant = "not-found" | "error";

interface NavItem {
    labelKey: string;
    href: string;
    target?: string;
    rel?: string;
}

export interface ErrorPageProps {
    /**
     * `"not-found"` (default) preserves the existing 404 catch-all screen,
     * rendered inside the dashboard shell (real Sidebar already present,
     * real Router context available).
     *
     * `"error"` is used by the error boundary. The boundary sits above
     * `BaseLayout` / `DashboardLayout` / `OnboardingWrapper` - and above the
     * router itself - so this variant CANNOT rely on react-router hooks
     * (`useNavigate`, `<Link>`) or on `Sidebar` (which needs `state.user`,
     * claims and roles that may be exactly what's broken). It paints its
     * own static nav strip with plain `<a href>` and navigates with
     * `window.location`, all hard navigations.
     */
    variant?: ErrorPageVariant;
}

// Deliberately not the `Sidebar` component (reads state.user, claims, plan
// roles and orgs - can be exactly what's partial/broken here) and
// deliberately <a href> instead of <Link>: a hard navigation is what clears
// state left dirty by the error (see error-boundary/index.tsx).
const DASHBOARD_NAV_ITEMS: NavItem[] = [
    { labelKey: "page-titles:home", href: "/home" },
    { labelKey: "page-titles:video-library", href: "/video-library" },
    { labelKey: "page-titles:switcher-cloud", href: "/switcher-cloud" },
    { labelKey: "page-titles:subscription", href: "/subscription" },
    { labelKey: "page-titles:profile", href: "/profile" },
    {
        labelKey: "page-titles:help-center",
        href: "https://support.switcherstudio.com",
        target: "_blank",
        rel: "noopener noreferrer"
    }
];

// `/getting-started` is the signup flow: there's no account yet, so links
// into the authenticated dashboard would just bounce off a login wall.
// Degrade to the two links that work without a session.
const NO_SESSION_NAV_ITEMS: NavItem[] = [
    { labelKey: "page-titles:login", href: "/login" },
    {
        labelKey: "page-titles:help-center",
        href: "https://support.switcherstudio.com",
        target: "_blank",
        rel: "noopener noreferrer"
    }
];

interface ErrorPageShellProps {
    header: string;
    subheader: string;
    description: string;
    actions: SwitcherButtonProps[];
    navItems?: NavItem[];
}

const ErrorPageShell: React.FC<ErrorPageShellProps> = ({
    header,
    subheader,
    description,
    actions,
    navItems = []
}) => {
    const { t } = useTranslation();

    return (
        <section className={styles["error-page"]}>
            <SwitcherLogo />
            <div className={styles["error-page-container"]}>
                <h1>{header}</h1>
                <h4>{subheader}</h4>
                <p>{description}</p>
                <div className={styles["actions-container"]}>
                    {actions.map((props, index) => (
                        <Button key={index} {...props} />
                    ))}
                </div>
            </div>
            {navItems.length > 0 && (
                <nav className={styles["error-page-nav"]}>
                    <ul>
                        {navItems.map((item) => (
                            <li key={item.href}>
                                <a
                                    href={item.href}
                                    target={item.target}
                                    rel={item.rel}
                                >
                                    {t(item.labelKey)}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </section>
    );
};

/**
 * The 404 catch-all (`path: "*"` in router/index.tsx). Rendered inside
 * `DashboardLayout`, so a real Router and a real Sidebar already surround
 * it - kept on client-side navigate() exactly as before.
 */
const NotFoundErrorPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleBackToHome = useCallback(() => {
        navigate("/");
    }, [navigate]);

    const actions = useMemo<SwitcherButtonProps[]>(
        () => [
            {
                children: t("error:action:back-to-home"),
                onClick: handleBackToHome
            }
        ],
        [handleBackToHome, t]
    );

    return (
        <ErrorPageShell
            header={t("error:not-found:header")}
            subheader={t("error:subheader")}
            description={t("error:description")}
            actions={actions}
        />
    );
};

/**
 * Rendered by `ErrorBoundary` (components/error-boundary), outside of any
 * Router context - no `useNavigate`/`<Link>` here, only `window.location`.
 */
const GenericErrorPage: React.FC = () => {
    const { t } = useTranslation();

    const handleRetry = useCallback(() => {
        window.location.reload();
    }, []);

    const handleBackToHomeHard = useCallback(() => {
        window.location.assign("/");
    }, []);

    const actions = useMemo<SwitcherButtonProps[]>(
        () => [
            {
                children: t("error:action:reentry"),
                onClick: handleRetry
            },
            {
                children: t("error:action:back-to-home"),
                onClick: handleBackToHomeHard,
                type: "outline"
            },
            {
                children: t("error:action:contact-support"),
                tag: "a",
                href: "mailto:support@switcherstudio.com",
                type: "text"
            }
        ],
        [handleRetry, handleBackToHomeHard, t]
    );

    const navItems = useMemo(() => {
        const isNoSessionRoute =
            typeof window !== "undefined" &&
            window.location.pathname.startsWith("/getting-started");
        return isNoSessionRoute ? NO_SESSION_NAV_ITEMS : DASHBOARD_NAV_ITEMS;
    }, []);

    return (
        <ErrorPageShell
            header={t("error:default:header")}
            subheader={t("error:subheader")}
            description={t("error:default:description")}
            actions={actions}
            navItems={navItems}
        />
    );
};

export const ErrorPage: React.FC<ErrorPageProps> = ({
    variant = "not-found"
}) => {
    return variant === "error" ? <GenericErrorPage /> : <NotFoundErrorPage />;
};
